import { HttpParams } from '@angular/common/http';
import { FilterOperator, QueryFilter } from '@shared/interfaces';

/**
 * Query parameters for paginated, sorted, and searchable API requests.
 *
 * @remarks
 * Adjust `QueryParams` to fit your needs.
 *
 * **NOTE:** Only **ADD** never **REMOVE**
 */
export interface QueryParams {
    /** The page number to retrieve. Defaults to `1` if not provided. */
    page?: number;
    /** The number of records per page. */
    limit?: number;
    /** The field name to sort results by. */
    sortBy?: string;
    /** The sort direction. Either `asc` or `desc`. */
    sortOrder?: 'asc' | 'desc' | 'ASC' | 'DESC';
    /** A search string to filter results by. */
    search?: string;
    /** Fields to search on */
    searchFields?: string | string[];

    /** Object for filtering */
    filters?: Record<string, FilterOperator> | QueryFilter | Record<string, string>;
    /** Fields to retrieve */
    fields?: string | string[];
}

/**
 * Builds an `HttpParams` object from the provided query parameters.
 * Skips any parameters that are `undefined`, `null`, or empty strings.
 *
 * @param params - Optional query parameters to serialize into `HttpParams`
 *
 * @returns A populated `HttpParams` instance ready to be passed to an HTTP request
 *
 * @example
 * const params = buildQueryParams({ page: 1, pageSize: 10, search: 'john', filters: {"name": { "_ilike": "john"}} });
 * this.http.get('/api/users', { params });
 */
export function buildQueryParams(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();

    if (!params) return httpParams;

    const { filters, ...rest } = params;

    if (!params) return httpParams;

    Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            httpParams = httpParams.set(key, value.toString());
        }
    });

    // Serialize filters as JSON string
    if (filters && Object.keys(filters).length > 0) {
        httpParams = httpParams.set('filters', JSON.stringify(filters));
    }

    return httpParams;
}
